import React, { useState } from "react"
import {
  Button,
  Modal,
  AutoComplete,
  Space,
  Typography,
  Row,
  Col,
  message,
  Input,
  Divider,
  Skeleton,
} from "antd"
import { ArrowRightOutlined } from "@ant-design/icons"
import axiosInstance from "../../../../axios/axiosConfig"
import { arrayUnion, doc, updateDoc } from "firebase/firestore"
import { db } from "../../../../firebase/config"
import { useAuthContext } from "../../../../hooks/useAuthContext"

const { Text, Link } = Typography
const { TextArea } = Input

export default function PmChangeKnife({ data }) {
  const { user } = useAuthContext()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [options, setOptions] = useState([])
  const [knivesLoading, setKnivesLoading] = useState(false)
  const [selectedKnife, setSelectedKnife] = useState(null)
  const [commentOnSwap, setCommentOnSwap] = useState(null)
  const [modalLoading, setModalLoading] = useState(false)

  const handleSearch = (value) => {
    let formattedSearchQuery = ""

    // Case 1: Input already in full "POLI...NAZ" format
    if (value.toUpperCase().startsWith("POLI") && value.toUpperCase().endsWith("NAZ")) {
      formattedSearchQuery = value.toUpperCase()
    }
    // Case 2: Input starts with "POLI" but does not end with "NAZ"
    else if (value.toUpperCase().startsWith("POLI")) {
      formattedSearchQuery = `${value.toUpperCase()}NAZ`
    }
    // Case 3: Input is numeric only
    else if (/^\d+$/.test(value)) {
      const formattedValue = value.padStart(3, "0") // Pad numeric value with leading zeros as needed
      formattedSearchQuery = `POLI${formattedValue}NAZ`
    }

    // Perform the search if there's a valid query
    if (formattedSearchQuery) {
      fetchKnives(formattedSearchQuery)
    } else {
      // Clear the options if the input does not match any expected pattern
      setOptions([])
    }
  }

  //fetches knives
  const fetchKnives = async (search) => {
    setKnivesLoading(true)
    try {
      const response = await axiosInstance.get(
        `/api/searchByCode?code=${encodeURIComponent(search)}`
      )
      let knivesData = []

      if (response.data && typeof response.data === "object") {
        knivesData = [response.data]
      } else if (Array.isArray(response.data)) {
        knivesData = response.data
      }

      // Create options with highlighted numeric part
      const formattedOptions = knivesData.map((knife) => {
        const numericPartMatch = knife.code.match(/\d+/) // Find the numeric part of the code
        const numericPart = numericPartMatch ? numericPartMatch[0] : ""
        const beforeNumeric = knife.code.split(numericPart)[0]
        const afterNumeric = knife.code.split(numericPart)[1]

        return {
          value: knife.code,
          rowss: knife.rowss,
          width: knife.width,
          height: knife.height,
          knifeCode: knife.code,
          id: knife.id,
          spaceBetweenRows: knife.spaceBetweenRows,
          label: (
            <div>
              {beforeNumeric}
              <span style={{ color: "#4096ff" }}>{numericPart}</span>
              {afterNumeric}
            </div>
          ),
        }
      })

      setOptions(formattedOptions)
    } catch (error) {
      console.error("Failed to fetch knives:", error)
      setOptions([])
    } finally {
      setKnivesLoading(false)
    }
  }

  const updateKnifeSelection = (value, options) => {
    setSelectedKnife(options)
    console.log(options)
    message.warning("selected")
  }

  const showModal = () => setIsModalOpen(true)

  const handleOk = async (e) => {
    e.preventDefault()
    setModalLoading(true)
    const ref = doc(db, "orders", data.id)

    const correctionTimestamp = new Date() // Client-side timestamp

    // Define the correction object
    const correction = {
      correctedAttribute: "knife",
      correctionComment: commentOnSwap,
      newValues: {
        labelWidth: selectedKnife.width,
        labelHeight: selectedKnife.height,
        knifeCode: selectedKnife.knifeCode,
        knifeId: selectedKnife.id,
        numberOfColumns: selectedKnife.rowss,
      },
      oldValues: {
        labelWidth: data.selectedProduct.labelWidth,
        labelHeight: data.selectedProduct.labelHeight,
        knifeCode: data.selectedProduct.knifeCode,
        knifeId: data.selectedProduct.knifeId,
        numberOfColumns: data.selectedProduct.numberOfColumns,
      },
      correctedBy: user.uid,
      correctionByEmail: user.email,
      correctionDate: correctionTimestamp,
      seen: false,
    }

    try {
      await updateDoc(ref, {
        "selectedProduct.labelWidth": selectedKnife.width,
        "selectedProduct.labelHeight": selectedKnife.height,
        "selectedProduct.knifeCode": selectedKnife.knifeCode,
        "selectedProduct.knifeId": selectedKnife.id,
        "selectedProduct.numberOfColumns": selectedKnife.rowss,
        orderCorrected: true,
        correctionHistory: arrayUnion(correction),
      }).then(() => {
        message.success("knife changed!")
      })
    } catch (err) {
      message.error("error happened")
      console.log(err)
    } finally {
      setModalLoading(false)
      setSelectedKnife(null)
      setCommentOnSwap(null)
      setOptions([])
      setIsModalOpen(false)
    }
  }

  const handleCancel = () => {
    setModalLoading(false)
    setSelectedKnife(null)
    setCommentOnSwap(null)
    setOptions([])
    setIsModalOpen(false)
  }

  return (
    <>
      <Button style={{ background: "#52c41a" }} block type="primary" onClick={showModal}>
        Change Knife
      </Button>
      <Modal
        title={"Change Knife"}
        open={isModalOpen}
        okButtonProps={{
          loading: modalLoading,
          disabled: selectedKnife === null ? true : false || commentOnSwap === null ? true : false,
        }}
        onCancel={handleCancel}
        onOk={handleOk}
        cancelButtonProps={{ loading: modalLoading }}>
        <Skeleton loading={modalLoading}>
          <Row>
            <Col span={24}>
              <Space>
                <Text>Current Knife:</Text>
                <Link>{data.selectedProduct.knifeCode}</Link>
                <ArrowRightOutlined />
                <AutoComplete
                  size="small"
                  style={{ width: 250 }}
                  loading={knivesLoading}
                  placeholder="Search and select knife"
                  onSearch={handleSearch}
                  options={options}
                  onSelect={updateKnifeSelection}
                />
              </Space>
            </Col>
            <Divider>Comment</Divider>
            <Col span={24}>
              <TextArea
                rows={4}
                placeholder="comment on change"
                value={commentOnSwap}
                onChange={(event) => setCommentOnSwap(event.target.value)}
              />
            </Col>
          </Row>
        </Skeleton>
      </Modal>
    </>
  )
}
